import { type ScheduleId } from '@solinkcorp/api-types';
import { useUserSession } from '@solinkcorp/authentication';
import { type ScheduledActivityEntity, ScheduledActivityRuleType } from 'clients/CallhomeApiTypes';
import CloudAiApi from 'clients/CloudAiApi';
import { useMemo } from 'react';
import useSWR from 'swr';

export type UseSpotCheckStructuredOutput = ReturnType<typeof useSpotCheckStructuredOutput>;

export function useSpotCheckStructuredOutput(message?: string) {
  const userSession = useUserSession();

  const spotCheckStructuredOutput = useSWR(
    message ? `spot-check-structured-output-${message}` : null,
    () => CloudAiApi.getStructuredOutput(userSession.accessToken, message as string),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      shouldRetryOnError: false,
    },
  );

  return spotCheckStructuredOutput;
}

const mapOfReoccuring: Record<string, { reoccurringCount: number; rule: string }> = {
  'Does Not Repeat': {
    reoccurringCount: 1,
    rule: '* * * * *',
  },
  'Every Hour': {
    reoccurringCount: -1,
    rule: '0 * * * *',
  },
  'Every 6 Hours': {
    reoccurringCount: -1,
    rule: '0 3,9,15,21 * * *',
  },
  Daily: {
    reoccurringCount: -1,
    rule: '0 0 * * *',
  },
  Weekly: {
    reoccurringCount: -1,
    rule: '0 0 * * 0',
  },
  Monthly: {
    reoccurringCount: -1,
    rule: '0 0 1 * *',
  },
  Annually: {
    reoccurringCount: -1,
    rule: '0 0 1 1 *',
  },
};

const mapOfHoursOfOperation: Record<
  'Inside Operating Hours' | 'Outside Operating Hours' | 'All Hours',
  { weeklyScheduleId: 'aiInferredOffHours' | null; inSchedulePeriods: boolean }
> = {
  'Inside Operating Hours': {
    weeklyScheduleId: 'aiInferredOffHours',
    inSchedulePeriods: false,
  },
  'Outside Operating Hours': {
    weeklyScheduleId: 'aiInferredOffHours',
    inSchedulePeriods: true,
  },
  'All Hours': {
    weeklyScheduleId: null,
    inSchedulePeriods: true,
  },
};

export function useFormattedSpotCheckStructuredOutput(message?: string) {
  const spotCheckStructuredOutput = useSpotCheckStructuredOutput(message);

  const formattedSpotCheckStructuredOutput = useMemo(() => {
    if (!spotCheckStructuredOutput.data) return undefined;

    const hoursOfOperation = spotCheckStructuredOutput?.data?.hoursOfOperation;

    const output: ScheduledActivityEntity = {
      id: 'structured-output' as ScheduleId,
      cameraIds: [],
      startsAt: 0,
      durationSecs: 0,
      isAlarmsEnabled: false,
      ruleType: ScheduledActivityRuleType.CRON,
      metadata: {
        aiChatPrompt: spotCheckStructuredOutput.data?.prompt,
        aiChatAnswerFormat: '',
        aiChatAnswerStates: spotCheckStructuredOutput.data?.states,
        // Restricting weeklyScheduleId to "aiInferredOffHours" | null
        ...(hoursOfOperation in mapOfHoursOfOperation
          ? mapOfHoursOfOperation[hoursOfOperation as keyof typeof mapOfHoursOfOperation]
          : {
              weeklyScheduleId: 'aiInferredOffHours', // Default to "aiInferredOffHours"
              inSchedulePeriods: false,
            }),
      },
      title: spotCheckStructuredOutput.data?.title,
      ...(mapOfReoccuring[spotCheckStructuredOutput?.data?.schedule] ?? {
        reoccurringCount: -1,
      }),
      endsAt: 0,
    };

    return output;
  }, [spotCheckStructuredOutput.data]);

  return { ...spotCheckStructuredOutput, data: formattedSpotCheckStructuredOutput };
}

export type UseFormattedSpotCheckStructuredOutput = ReturnType<
  typeof useFormattedSpotCheckStructuredOutput
>;
